import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

internal data class TourLaneScoreInput(val entryId: String, val games: List<Int?>, val premiums: Int)
private data class TourLaneDraft(val games: List<String>, val premiums: String)

@Composable
internal fun TourLaneScoreTable(rows: List<TourStageRow>, awards: List<TourPerformanceAward>, phase: String,
    revision: Long, label: String, editable: Boolean, disabled: Boolean,
    onDirty: (Boolean) -> Unit, onSave: (List<TourLaneScoreInput>) -> Unit, onWithdraw: (TourStageRow) -> Unit) {
    val initial = remember(revision, phase, rows) { rows.associate { row ->
        val games = when (phase) {
            "final" -> listOf(row.game3)
            "semifinal" -> listOf(row.game1, row.game2)
            else -> listOf(row.game1, row.game2, row.game3)
        }
        row.entryId to TourLaneDraft(games.map { it?.toString().orEmpty() }, awards.filter { it.entryId == row.entryId && it.phase == phase }.sumOf { it.quantity }.toString())
    } }
    var drafts by remember(initial) { mutableStateOf(initial) }
    val dirty = drafts != initial
    fun change(entry: String, value: TourLaneDraft) {
        drafts = drafts + (entry to value); onDirty(drafts != initial)
    }
    val valid = drafts.values.all { draft ->
        draft.games.all { it.isBlank() || it.trim().toIntOrNull()?.let { n -> n in 0..300 } == true } &&
            (draft.premiums.trim().ifEmpty { "0" }.toIntOrNull()?.let { n -> n in 0..999 } == true) &&
            (draft.premiums.trim().ifEmpty { "0" }.toIntOrNull() == 0 || draft.games.any { it.isNotBlank() })
    }
    Form(attrs = { classes("tour-lane-score-form"); onSubmit {
        it.preventDefault()
        if (editable && !disabled && dirty && valid) onSave(rows.map { row ->
            val draft = drafts.getValue(row.entryId)
            TourLaneScoreInput(row.entryId, draft.games.map { value -> value.trim().takeIf { it.isNotEmpty() }?.toInt() }, draft.premiums.trim().ifEmpty { "0" }.toInt())
        })
    } }) {
        Div(attrs = { classes("table-scroll") }) {
            Table(attrs = { classes("public-table", "tour-score-table", "tour-lane-score-table"); attr("aria-label", "Náhozy ${tourLabel(phase, TOUR_PHASES)}") }) {
                Thead { Tr {
                    listOf("Pořadí", "Hráč", "Pozice", "HDCP", "1. hra", "2. hra").forEach { Th { Text(it) } }
                    if (phase != "semifinal") Th { Text("3. hra") }
                    listOf("Prémie", "Bez HDCP", "S HDCP", "Stav").forEach { Th { Text(it) } }
                    if (editable) Th {}
                } }
                Tbody {
                    Tr(attrs = { classes("tour-lane-heading") }) { Th(attrs = { colspan((if (phase == "semifinal") 10 else 11) + if (editable) 1 else 0); attr("scope", "rowgroup") }) { Text(label) } }
                    rows.forEach { row -> key(row.entryId) {
                        val draft = drafts.getValue(row.entryId)
                        Tr(attrs = { attr("data-entry-id", row.entryId); attr("data-lane", row.lane?.toString().orEmpty()); attr("data-position", row.position?.toString().orEmpty()) }) {
                            Td { Text(row.rank?.toString() ?: if (row.tiePending) "Shoda" else "—") }
                            Td(attrs = { classes("tour-player-name") }) { Text(row.playerName) }
                            Td { Text(row.position?.toString() ?: "—") }; Td { Text(row.handicap.toString()) }
                            (1..if (phase == "semifinal") 2 else 3).forEach { game -> Td {
                                if (editable && (phase != "final" || game == 3)) {
                                    val index = if (phase == "final") 0 else game - 1
                                    Input(type = InputType.Number) {
                                        classes("tour-score-input"); attr("aria-label", "${row.playerName} · $game. hra")
                                        attr("min", "0"); attr("max", "300"); attr("step", "1"); attr("inputmode", "numeric")
                                        value(draft.games[index]); if (disabled) disabled()
                                        onInput { event ->
                                            val latest = drafts.getValue(row.entryId)
                                            change(row.entryId, latest.copy(games = latest.games.mapIndexed { i, old -> if (i == index) event.target.value else old }))
                                        }
                                    }
                                } else Text(listOf(row.game1, row.game2, row.game3)[game - 1]?.toString() ?: "—")
                            } }
                            Td {
                                if (editable) Input(type = InputType.Number) {
                                    classes("tour-score-input"); attr("aria-label", "${row.playerName} · Prémie")
                                    attr("min", "0"); attr("max", "999"); attr("step", "1"); attr("inputmode", "numeric")
                                    value(draft.premiums); if (disabled) disabled()
                                    onInput { change(row.entryId, drafts.getValue(row.entryId).copy(premiums = it.target.value)) }
                                } else Text(draft.premiums)
                            }
                            Td { Text(row.rawTotal?.toString() ?: "—") }; Td { Text(row.adjustedTotal?.toString() ?: "—") }
                            Td { Text(row.withdrawnPhase?.let { "Odstoupil · ${tourLabel(it, TOUR_PHASES)}" } ?: if (row.complete) "Dohráno" else "${row.played}/${if (phase == "semifinal") 2 else 3} her") }
                            if (editable) Td { Button(attrs = {
                                type(ButtonType.Button); if (disabled || dirty) disabled(); onClick { onWithdraw(row) }
                            }) { Text(if (row.withdrawnPhase == null) "Odstoupil ${row.playerName}" else "Obnovit účast ${row.playerName}") } }
                        }
                    } }
                }
            }
        }
        if (editable) {
            if (!valid) P(attrs = { classes("editor-error"); attr("role", "alert") }) { Text("Náhozy musí být celá čísla 0–300 nebo prázdné. Prémie: počet 0–999, pouze s odehranou hrou v této fázi.") }
            Div(attrs = { classes("tour-actions") }) {
                Button(attrs = { classes("primary"); type(ButtonType.Submit); if (disabled || !dirty || !valid) disabled() }) { Text("Uložit dráhu") }
                if (dirty) Button(attrs = { type(ButtonType.Button); if (disabled) disabled(); onClick { drafts = initial; onDirty(false) } }) { Text("Zrušit změny") }
            }
        }
    }
}
